import { c as _c } from "react-compiler-runtime";
import { useMemo, useState } from 'react';
import { useFetchDefaultPinnedView } from 'customer-data-views-management/pinnedViews/hooks/useFetchDefaultPinnedView';
import { useFetchPinnedViews } from 'customer-data-views-management/pinnedViews/hooks/useFetchPinnedViews';
import { useFetchSingleView } from 'customer-data-views-management/singleView/hooks/useFetchSingleView';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import { useIsFoldersMode } from 'framework-listing-lib/internal/hooks/useUIMode';
import { convertCustomerDataViewToView } from '../utils/mapper';
import { useHasViews, useViewPropsCurrentViewId, useViewsNamespace } from './useViewProps';
function useInitialViewId(t0) {
  "use memo";

  const {
    currentViewId,
    objectTypeId,
    namespace
  } = t0;
  const [initialViewId, setInitialViewId] = useState(currentViewId);
  if (!initialViewId && currentViewId) {
    setInitialViewId(currentViewId);
  }
  const [prevObjectTypeId, setPrevObjectTypeId] = useState(objectTypeId);
  if (prevObjectTypeId !== objectTypeId) {
    setPrevObjectTypeId(objectTypeId);
    setInitialViewId(currentViewId);
  }
  const [prevNamespace, setPrevNamespace] = useState(namespace);
  if (prevNamespace !== namespace) {
    setPrevNamespace(namespace);
    setInitialViewId(currentViewId);
  }
  return initialViewId;
}
export function useFetchCurrentView() {
  "use memo";

  var _pinnedViewsData$pinn;
  const $ = _c(34);
  const objectTypeId = useObjectTypeId();
  const namespace = useViewsNamespace();
  const hasViews = useHasViews();
  const isFoldersMode = useIsFoldersMode();
  const currentViewIdFromProps = useViewPropsCurrentViewId();
  let t0;
  if ($[0] !== currentViewIdFromProps || $[1] !== namespace || $[2] !== objectTypeId) {
    t0 = {
      currentViewId: currentViewIdFromProps,
      objectTypeId,
      namespace
    };
    $[0] = currentViewIdFromProps;
    $[1] = namespace;
    $[2] = objectTypeId;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const initialViewId = useInitialViewId(t0);
  const t1 = !hasViews;
  let t2;
  if ($[4] !== initialViewId || $[5] !== namespace || $[6] !== objectTypeId || $[7] !== t1) {
    t2 = {
      objectTypeId,
      namespace,
      viewId: initialViewId,
      skip: t1
    };
    $[4] = initialViewId;
    $[5] = namespace;
    $[6] = objectTypeId;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const {
    data: defaultPinnedViewData,
    loading: loadingDefaultPinnedView,
    error: defaultPinnedViewError
  } = useFetchDefaultPinnedView(t2);
  const t3 = hasViews ? currentViewIdFromProps : undefined;
  let t4;
  if ($[9] !== namespace || $[10] !== objectTypeId || $[11] !== t3) {
    t4 = {
      objectTypeId,
      namespaceName: namespace,
      id: t3
    };
    $[9] = namespace;
    $[10] = objectTypeId;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const {
    data: currentViewData,
    loading: loadingCurrentView,
    error: currentViewError
  } = useFetchSingleView(t4);
  let t5;
  if ($[13] !== namespace || $[14] !== objectTypeId) {
    t5 = {
      objectTypeId,
      namespace
    };
    $[13] = namespace;
    $[14] = objectTypeId;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const {
    data: pinnedViewsData
  } = useFetchPinnedViews(t5);
  const t6 = defaultPinnedViewError ? pinnedViewsData === null || pinnedViewsData === void 0 || (_pinnedViewsData$pinn = pinnedViewsData.pinnedViews) === null || _pinnedViewsData$pinn === void 0 ? void 0 : _pinnedViewsData$pinn[0] : undefined;
  let t7;
  if ($[16] !== namespace || $[17] !== objectTypeId || $[18] !== t6) {
    t7 = {
      objectTypeId,
      namespaceName: namespace,
      id: t6
    };
    $[16] = namespace;
    $[17] = objectTypeId;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const {
    data: fallbackDefaultPinnedViewData
  } = useFetchSingleView(t7);
  const rawDefaultPinnedView = (defaultPinnedViewData === null || defaultPinnedViewData === void 0 ? void 0 : defaultPinnedViewData.defaultPinnedView) || (fallbackDefaultPinnedViewData === null || fallbackDefaultPinnedViewData === void 0 ? void 0 : fallbackDefaultPinnedViewData.singleViewById);
  let t8;
  if ($[20] !== rawDefaultPinnedView) {
    t8 = rawDefaultPinnedView ? convertCustomerDataViewToView(rawDefaultPinnedView) : undefined;
    $[20] = rawDefaultPinnedView;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const defaultPinnedView = t8;
  let t9;
  if ($[22] !== currentViewData) {
    t9 = currentViewData !== null && currentViewData !== void 0 && currentViewData.singleViewById ? convertCustomerDataViewToView(currentViewData.singleViewById) : undefined;
    $[22] = currentViewData;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const currentView = t9;
  if (!hasViews || isFoldersMode) {
    let t10;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = {
        loading: false,
        currentView: undefined
      };
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    return t10;
  }
  if (!currentViewIdFromProps) {
    let t10;
    if ($[25] !== defaultPinnedView || $[26] !== loadingDefaultPinnedView) {
      t10 = {
        loading: loadingDefaultPinnedView,
        currentView: defaultPinnedView
      };
      $[25] = defaultPinnedView;
      $[26] = loadingDefaultPinnedView;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    return t10;
  }
  if (currentViewError && defaultPinnedView) {
    let t10;
    if ($[28] !== defaultPinnedView) {
      t10 = {
        loading: false,
        currentView: defaultPinnedView
      };
      $[28] = defaultPinnedView;
      $[29] = t10;
    } else {
      t10 = $[29];
    }
    return t10;
  }
  if (currentViewError && !loadingDefaultPinnedView) {
    let t10;
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = {
        loading: false,
        currentView: undefined
      };
      $[30] = t10;
    } else {
      t10 = $[30];
    }
    return t10;
  }
  let t10;
  if ($[31] !== currentView || $[32] !== loadingCurrentView) {
    t10 = {
      loading: loadingCurrentView,
      currentView
    };
    $[31] = currentView;
    $[32] = loadingCurrentView;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  return t10;
}